/**
 * SuperAdminLogin — Microsoft-style staff sign-in surface (Feb 2026).
 *
 * Stripped to the essentials: ZHELTA logo + "Sign in to ZHELTA" + email +
 * password + "Forgot password?" link. NO divisions visible pre-auth.
 * After credentials verify, the 2FA challenge step appears (mandatory for
 * all staff). Forgot-password issues a request that the super admin must
 * approve from `/admin/v2/password-resets` — never auto-mails a link.
 *
 * Backwards-compatible: the legacy `division` prop is now a no-op (kept
 * only so the existing /zhelta-internal/{slug} routes continue to render
 * this component without crashing). Post-auth division routing is handled
 * by `admin_v2/AdminV2App` via the SelectDivisionPage gate.
 *
 * After auth this component drops the user at `/admin/v2`. The unified
 * console handles must-change-password, must-setup-2fa, division
 * selection, and RBAC scoping.
 */
import React, { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { useAuth, formatApiError } from '../context/AuthContext';
import { ShieldCheck, ArrowLeft, CheckCircle2 } from 'lucide-react';
import ZheltaLogo from '../components/ZheltaLogo';

const ADMIN_ROLES = new Set(['super_admin', 'admin', 'manager', 'staff', 'sales', 'technician']);

export default function SuperAdminLogin({ division: _ignored = null }) {
  const { login, loginWith2fa } = useAuth();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const next = params.get('next') || '/admin/v2';

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  // 2FA challenge state.
  const [challenge, setChallenge] = useState('');
  const [code, setCode] = useState('');
  const [useRecovery, setUseRecovery] = useState(false);

  // Forgot-password flow state.
  const [view, setView] = useState('signin'); // 'signin' | 'forgot' | 'forgot_sent'
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotLoading, setForgotLoading] = useState(false);
  const [forgotError, setForgotError] = useState('');

  const finishLogin = (role) => {
    if (!ADMIN_ROLES.has(role)) {
      setError('This portal is for ZHELTA staff only.');
      setLoading(false);
      return;
    }
    try { localStorage.setItem('zhelta_admin_pw', password); } catch { /* ignore */ }
    navigate(next.startsWith('/admin') ? next : '/admin/v2', { replace: true });
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const res = await login(email.trim(), password);
      if (res?.requires_2fa) {
        setChallenge(res.challenge);
        setLoading(false);
        return;
      }
      // /auth/login returns role at top level (and also nested under .user
      // when called via /auth/2fa/login). Read both shapes for safety.
      finishLogin(res?.role || res?.user?.role);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || 'Wrong email or password.');
      setLoading(false);
    }
  };

  const onSubmit2fa = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const trimmed = useRecovery ? code.trim() : code;
      if (useRecovery && !/^[0-9a-f]{8}-[0-9a-f]{8}$/i.test(trimmed)) {
        setError('Enter a recovery code in the form xxxxxxxx-xxxxxxxx.');
        setLoading(false);
        return;
      }
      if (!useRecovery && trimmed.length !== 6) {
        setError('Enter the 6-digit code from your authenticator app.');
        setLoading(false);
        return;
      }
      const res = await loginWith2fa(challenge, trimmed);
      finishLogin(res?.role || res?.user?.role);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || 'Verification failed. Try again.');
      setLoading(false);
    }
  };

  const cancel2fa = () => {
    setChallenge('');
    setCode('');
    setError('');
    setLoading(false);
    setUseRecovery(false);
  };

  const onForgotSubmit = async (e) => {
    e.preventDefault();
    setForgotError('');
    setForgotLoading(true);
    try {
      const url = `${process.env.CRA_API_BASE_URL || process.env.REACT_APP_API_BASE_URL || process.env.REACT_APP_BACKEND_URL}/api/auth/forgot-password`;
      await axios.post(url, { email: forgotEmail.trim().toLowerCase() }, { withCredentials: true });
      setView('forgot_sent');
    } catch (err) {
      setForgotError(formatApiError(err.response?.data?.detail) || 'Could not submit request. Try again later.');
    } finally {
      setForgotLoading(false);
    }
  };

  return (
    <div
      className="min-h-screen flex items-center justify-center px-4 py-12 bg-[#f3f3f3]"
      data-testid="super-admin-login"
      style={{ fontFamily: '"Segoe UI", system-ui, -apple-system, BlinkMacSystemFont, sans-serif' }}
    >
      <div className="w-full max-w-[440px]">
        {/* Microsoft-style flat tile */}
        <div className="bg-white shadow-[0_2px_6px_rgba(0,0,0,0.20),0_18px_40px_rgba(0,0,0,0.12)] px-11 py-10">
          {/* Brand row — ZHELTA shield + wordmark, equivalent to MS's 4-color tile */}
          <Link to="/" className="inline-flex items-center gap-2 mb-6 group" data-testid="brand-home">
            <ZheltaLogo size={28} />
            <span className="text-[15px] font-semibold tracking-[0.2em] text-[#1a1a1a]">ZHELTA</span>
          </Link>

          {/* Sign-in form */}
          {view === 'signin' && !challenge && (
            <div>
              <h1 className="text-[24px] font-semibold text-[#1a1a1a] leading-tight mb-1">Sign in</h1>
              <p className="text-[13px] text-[#605e5c] mb-6">to continue to ZHELTA Operations</p>
              <form onSubmit={onSubmit} data-testid="super-admin-form">
                <div className="mb-4">
                  <input
                    type="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    data-testid="super-admin-email"
                    className="w-full px-0 pt-1 pb-1.5 bg-transparent border-0 border-b-2 border-[#605e5c] focus:border-[#f5a623] outline-none text-[15px] text-[#1a1a1a] placeholder-[#a19f9d] rounded-none transition-colors"
                    placeholder="Email, phone, or username"
                    autoFocus
                  />
                </div>
                <div className="mb-4">
                  <input
                    type="password"
                    required
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    data-testid="super-admin-password"
                    className="w-full px-0 pt-1 pb-1.5 bg-transparent border-0 border-b-2 border-[#605e5c] focus:border-[#f5a623] outline-none text-[15px] text-[#1a1a1a] placeholder-[#a19f9d] rounded-none transition-colors"
                    placeholder="Password"
                  />
                </div>

                {error && (
                  <div data-testid="super-admin-error" className="text-[#d83b01] text-[13px] py-2 mb-1 leading-snug">
                    {error}
                  </div>
                )}

                <div className="text-[13px] mb-4">
                  <button
                    type="button"
                    onClick={() => { setView('forgot'); setForgotEmail(email); setForgotError(''); }}
                    data-testid="super-admin-forgot"
                    className="text-[#f5a623] hover:underline font-normal"
                  >
                    Forgot password?
                  </button>
                </div>

                <div className="flex justify-end pt-2">
                  <button
                    type="submit"
                    disabled={loading}
                    data-testid="super-admin-submit"
                    className="min-w-[108px] px-6 py-2.5 text-[15px] font-semibold text-[#0e0e10] bg-[#f5a623] hover:bg-[#e09611] disabled:opacity-60 transition-colors rounded-none"
                  >
                    {loading ? 'Signing in…' : 'Sign in'}
                  </button>
                </div>
              </form>
            </div>
          )}

          {/* 2FA challenge */}
          {challenge && (
            <div>
              <h1 className="text-[24px] font-semibold text-[#1a1a1a] leading-tight mb-1">Verify your identity</h1>
              <p className="text-[13px] text-[#605e5c] mb-6">{email || 'staff sign-in'}</p>
              <form onSubmit={onSubmit2fa} data-testid="super-admin-2fa-form">
                {!useRecovery ? (
                  <p className="text-[13.5px] text-[#1a1a1a] leading-relaxed mb-5">
                    We've sent a 6-digit code to <span className="font-semibold">{email || 'your email'}</span>.
                    You can also use the code from your authenticator app — whichever arrives first.
                  </p>
                ) : (
                  <p className="text-[13.5px] text-[#1a1a1a] leading-relaxed mb-5">
                    Enter one of your saved <strong>recovery codes</strong>. Each code can be used once.
                  </p>
                )}

                <div className="mb-4">
                  {useRecovery ? (
                    <input
                      type="text"
                      autoComplete="off"
                      value={code}
                      onChange={(e) => setCode(e.target.value.toLowerCase().replace(/[^0-9a-f-]/g, '').slice(0, 17))}
                      data-testid="super-admin-2fa-recovery-code"
                      className="w-full px-0 pt-1 pb-1.5 bg-transparent border-0 border-b-2 border-[#605e5c] focus:border-[#f5a623] outline-none text-[15px] tracking-[0.12em] font-mono rounded-none transition-colors"
                      placeholder="xxxxxxxx-xxxxxxxx"
                      autoFocus
                    />
                  ) : (
                    <input
                      type="text"
                      inputMode="numeric"
                      autoComplete="one-time-code"
                      pattern="[0-9]{6}"
                      maxLength={6}
                      required
                      value={code}
                      onChange={(e) => setCode(e.target.value.replace(/[^0-9]/g, '').slice(0, 6))}
                      data-testid="super-admin-2fa-code"
                      className="w-full px-0 pt-1 pb-1.5 bg-transparent border-0 border-b-2 border-[#605e5c] focus:border-[#f5a623] outline-none text-[22px] tracking-[0.4em] font-mono rounded-none transition-colors"
                      placeholder="Code"
                      autoFocus
                    />
                  )}
                </div>

                {error && (
                  <div data-testid="super-admin-2fa-error" className="text-[#d83b01] text-[13px] py-2 mb-2 leading-snug">
                    {error}
                  </div>
                )}

                <div className="text-[13px] mb-4 flex flex-col gap-1.5 items-start">
                  <button
                    type="button"
                    onClick={() => { setUseRecovery((v) => !v); setCode(''); setError(''); }}
                    data-testid="super-admin-2fa-toggle-recovery"
                    className="text-[#f5a623] hover:underline"
                  >
                    {useRecovery ? 'Use authenticator code instead' : 'Use a recovery code instead'}
                  </button>
                  <button
                    type="button"
                    onClick={cancel2fa}
                    data-testid="super-admin-2fa-cancel"
                    className="text-[#605e5c] hover:text-[#1a1a1a] hover:underline"
                  >
                    Sign in with a different account
                  </button>
                </div>

                <div className="flex justify-end pt-2">
                  <button
                    type="submit"
                    disabled={loading || (useRecovery ? code.replace(/-/g, '').length !== 16 : code.length !== 6)}
                    data-testid="super-admin-2fa-submit"
                    className="min-w-[108px] px-6 py-2.5 text-[15px] font-semibold text-[#0e0e10] bg-[#f5a623] hover:bg-[#e09611] disabled:opacity-60 transition-colors rounded-none"
                  >
                    {loading ? 'Verifying…' : 'Verify'}
                  </button>
                </div>
              </form>
            </div>
          )}

          {/* Forgot password — request approval */}
          {view === 'forgot' && !challenge && (
            <div>
              <button
                type="button"
                onClick={() => { setView('signin'); setForgotError(''); }}
                data-testid="super-admin-forgot-back"
                className="text-[#605e5c] hover:text-[#1a1a1a] text-[13px] mb-3 inline-flex items-center gap-1"
              >
                <ArrowLeft size={12} /> Back
              </button>
              <h1 className="text-[24px] font-semibold text-[#1a1a1a] leading-tight mb-1">Forgot password</h1>
              <p className="text-[13px] text-[#605e5c] mb-6">A super admin will approve the reset.</p>
              <form onSubmit={onForgotSubmit} data-testid="super-admin-forgot-form">
                <p className="text-[13px] text-[#1a1a1a] leading-relaxed mb-5">
                  Enter your work email. Your super admin will be notified and must approve the reset before a magic link is emailed to you.
                </p>
                <div className="mb-5">
                  <input
                    type="email"
                    required
                    value={forgotEmail}
                    onChange={(e) => setForgotEmail(e.target.value)}
                    data-testid="super-admin-forgot-email"
                    className="w-full px-0 pt-1 pb-1.5 bg-transparent border-0 border-b-2 border-[#605e5c] focus:border-[#f5a623] outline-none text-[15px] text-[#1a1a1a] placeholder-[#a19f9d] rounded-none transition-colors"
                    placeholder="Work email"
                    autoFocus
                  />
                </div>
                {forgotError && (
                  <div data-testid="super-admin-forgot-error" className="text-[#d83b01] text-[13px] py-2 mb-2 leading-snug">
                    {forgotError}
                  </div>
                )}
                <div className="flex justify-end pt-2">
                  <button
                    type="submit"
                    disabled={forgotLoading || !forgotEmail.trim()}
                    data-testid="super-admin-forgot-submit"
                    className="min-w-[120px] px-6 py-2.5 text-[15px] font-semibold text-[#0e0e10] bg-[#f5a623] hover:bg-[#e09611] disabled:opacity-60 transition-colors rounded-none"
                  >
                    {forgotLoading ? 'Sending…' : 'Submit'}
                  </button>
                </div>
              </form>
            </div>
          )}

          {/* Forgot password — confirmation */}
          {view === 'forgot_sent' && (
            <div data-testid="super-admin-forgot-sent">
              <div className="w-12 h-12 bg-emerald-50 border border-emerald-200 mb-5 flex items-center justify-center">
                <CheckCircle2 className="w-6 h-6 text-emerald-600" strokeWidth={2.2} />
              </div>
              <h1 className="text-[24px] font-semibold text-[#1a1a1a] leading-tight mb-1">Request submitted</h1>
              <p className="text-[13px] text-[#605e5c] leading-relaxed mb-6">
                Your super admin has been notified. Once approved, you'll receive a reset link at <strong>{forgotEmail}</strong>. If you don't see it within a few hours, contact your super admin directly.
              </p>
              <button
                type="button"
                onClick={() => { setView('signin'); }}
                data-testid="super-admin-forgot-done"
                className="text-[#f5a623] hover:underline text-[13px]"
              >
                Back to sign-in
              </button>
            </div>
          )}
        </div>

        {/* Footer (Microsoft-style: small links at bottom-right of tile area) */}
        <div className="flex justify-end gap-4 mt-2 pr-1 text-[12px] text-[#605e5c]">
          <span data-testid="login-meta-personnel">Authorized personnel only</span>
          <span aria-hidden="true">·</span>
          <span data-testid="login-meta-2fa">2FA enforced</span>
        </div>
      </div>
    </div>
  );
}
